export const modelVS = `#version 300 es
layout(location = 0) in vec3 aPosition;
layout(location = 1) in vec3 aNormal;
layout(location = 2) in vec2 aUv;

uniform mat4 uMvp;
uniform mat4 uModel;

out vec3 vNormal;
out vec2 vUv;

void main() {
  vNormal = mat3(uModel) * aNormal;
  vUv = aUv;
  gl_Position = uMvp * vec4(aPosition, 1.0);
}
`;

export const modelFS = `#version 300 es
precision highp float;

in vec3 vNormal;
in vec2 vUv;
out vec4 outColor;

uniform vec3 uColor;
uniform bool uUseTexture;
// Alpha cutout for the menu text planes (plan 17): their RGBA textures carry
// hard-edged bubble letters on a transparent background, and discarding below
// 0.5 keeps depth correct with no blend sorting. Gated so bugs are untouched.
uniform bool uAlphaCutout;
uniform sampler2D uTexture;

const vec3 LIGHT_DIR = normalize(vec3(0.4, 0.7, 0.6));

void main() {
  vec4 texel = texture(uTexture, vUv);
  if (uAlphaCutout && texel.a < 0.5) discard;
  vec3 albedo = uUseTexture ? texel.rgb : uColor;
  vec3 n = normalize(vNormal);
  // Two-sided lambert: culling is off for models, so flip the normal to face
  // the light when a back face shows (thin legs, single-sided bits).
  float diffuse = abs(dot(n, LIGHT_DIR));
  float ambient = 0.5;
  outColor = vec4(albedo * (ambient + 0.65 * diffuse), 1.0);
}
`;

export const cubeVS = `#version 300 es
layout(location = 0) in vec3 aPosition;
layout(location = 1) in vec3 aNormal;
layout(location = 2) in vec2 aUv;
layout(location = 3) in float aMaterial;
layout(location = 4) in float aTint;

uniform mat4 uMvp;
uniform mat4 uModel;

out vec3 vNormal;
out vec2 vUv;
out float vMaterial;
out float vTint;

void main() {
  vNormal = mat3(uModel) * aNormal;
  vUv = aUv;
  vMaterial = aMaterial;
  vTint = aTint;
  gl_Position = uMvp * vec4(aPosition, 1.0);
}
`;

export const cubeFS = `#version 300 es
precision highp float;

in vec3 vNormal;
in vec2 vUv;
in float vMaterial;
in float vTint;
out vec4 outColor;

// Original block atlas (subblock_1536_tex.png): 3x3 grid of 512px tiles.
// Tile coordinates count from the bottom (the texture is flipped on upload).
// The atlas rows are ONION DEPTHS, not block states (unity-overview.md §6):
// row 2 is the grass crust, row 1 the magma layer beneath it, and (0,0) is the
// stone used for faces pointing inward. The index is a TileId from
// render/onion.ts, which mirrors what the original's per-zone block meshes
// bake into their own UVs. (No backticks in here: this is a template literal.)
uniform sampler2D uAtlas;

const vec3 LIGHT_DIR = normalize(vec3(0.4, 0.7, 0.6));

const vec2 TILES[5] = vec2[5](
  vec2(1.0, 2.0), // 0 grass top   - depth 0, outward
  vec2(0.0, 2.0), // 1 grass side  - depth 0, tangential
  vec2(0.0, 1.0), // 2 magma top   - depth 1+, outward
  vec2(1.0, 1.0), // 3 magma side  - depth 1, tangential
  vec2(0.0, 0.0)  // 4 stone       - any depth, inward
);

// An infected block keeps its depth's tile and is scorched instead. The
// original shows infection not at all (unity-overview.md §6: no material swap
// exists, and its Infected stage lasts one frame), so this is a port of the
// scrapped intent rather than of shipped behaviour: our infection spans three
// bites over several seconds and has to be legible.
const vec3 INFECTED_TINT = vec3(0.62, 0.30, 0.22);

vec2 atlasUv(vec2 uv, vec2 tile) {
  // Inset each tile slightly so mipmapped sampling does not bleed neighbours.
  return (tile + 0.012 + uv * 0.976) / 3.0;
}

void main() {
  vec2 uv = atlasUv(vUv, TILES[int(vMaterial + 0.5)]);
  vec3 albedo = texture(uAtlas, uv).rgb;
  albedo = mix(albedo, albedo * INFECTED_TINT, vTint);

  // Production-matched lighting: the original renders the grass tile at
  // ~1.65x its raw brightness on the facing side (measured off a production
  // screenshot), with no seam or edge darkening — the punchy, almost unlit
  // look comes from over-driving the texture.
  vec3 n = normalize(vNormal);
  float diffuse = max(dot(n, LIGHT_DIR), 0.0);
  outColor = vec4(albedo * (1.15 + 0.85 * diffuse), 1.0);
}
`;

// Ghost shells: the translucent husk a destroyed block leaves behind. Reuses
// the cube vertex shader (same attribute layout) and ignores the atlas
// entirely — the original's material has no _MainTex, just a colour with alpha
// and an emissive term (Materials/subblock_ghost_1.mat, unity-overview.md 6):
//   _Color     = (0.137, 0.682, 0.0, 0.463)
//   _EmisColor = (0.0,   0.682, 0.860, 0.898)
export const ghostFS = `#version 300 es
precision highp float;

in vec3 vNormal;
in vec2 vUv;
in float vMaterial;
in float vTint;
out vec4 outColor;

const vec3 LIGHT_DIR = normalize(vec3(0.4, 0.7, 0.6));
const vec4 GHOST_COLOR = vec4(0.137, 0.682, 0.0, 0.463);
const vec3 GHOST_EMISSIVE = vec3(0.0, 0.682, 0.860);

void main() {
  vec3 n = normalize(vNormal);
  float diffuse = max(dot(n, LIGHT_DIR), 0.0);
  // The colour and alpha are measured off the original's material; this weight
  // on the emissive is not — it is the one free parameter here. Keep it low:
  // the shell has to stay glass. At 0.35 two stacked shells turned the magma
  // behind them into cyan mud, which is the failure mode to watch for.
  vec3 rgb = GHOST_COLOR.rgb * (0.7 + 0.3 * diffuse) + GHOST_EMISSIVE * 0.12;
  outColor = vec4(rgb, GHOST_COLOR.a);
}
`;
